import React from "react";
import useTesterFeedback from "../../hooks/useTesterFeedback";
import ScreenshotThumbnail from "./ScreenshotThumbnail";
import { Bug, Star, CheckCircle, Clock, AlertTriangle } from "lucide-react";

const TYPE_CONFIG = {
  bug: { label: "Bug", color: "bg-red-500/10 text-red-400", icon: Bug },
  "ease-of-use": { label: "Usability", color: "bg-blue-500/10 text-blue-400", icon: Star },
  "feature-completeness": { label: "Features", color: "bg-purple-500/10 text-purple-400", icon: CheckCircle },
  design: { label: "Design", color: "bg-pink-500/10 text-pink-400", icon: Star },
  performance: { label: "Performance", color: "bg-yellow-500/10 text-yellow-400", icon: Clock },
  overall: { label: "Overall", color: "bg-emerald-500/10 text-emerald-400", icon: Star },
};

export default function TesterFeedbackHistory() {
  const { pastFeedback, loading, updateBugStatus } = useTesterFeedback();

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="w-5 h-5 border-2 border-slate-700 border-t-cyan-400 rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (pastFeedback.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-slate-500">No feedback submitted yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-[400px] overflow-y-auto">
      {pastFeedback.map((item) => {
        const config = TYPE_CONFIG[item.feedbackType] || TYPE_CONFIG.overall;
        const Icon = config.icon;
        const date = item.dateSubmitted?.toDate?.()
          ? item.dateSubmitted.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric" })
          : "N/A";

        return (
          <div
            key={item.id}
            className="p-3 rounded-xl border border-slate-700 bg-slate-800/50 space-y-2"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${config.color}`}>
                  <Icon size={10} />
                  {config.label}
                </span>
                {item.severity && (
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                    item.severity === "blocker" ? "bg-red-500/20 text-red-400" :
                    item.severity === "major" ? "bg-orange-500/20 text-orange-400" :
                    item.severity === "minor" ? "bg-yellow-500/20 text-yellow-400" :
                    "bg-slate-700 text-slate-400"
                  }`}>
                    {item.severity}
                  </span>
                )}
              </div>
              <span className="text-[10px] text-slate-500">{date}</span>
            </div>

            <p className="text-xs text-slate-300 line-clamp-2">{item.message}</p>

            {/* Screenshots */}
            {item.screenshots?.length > 0 && (
              <ScreenshotThumbnail screenshots={item.screenshots} size={36} maxVisible={4} />
            )}

            {/* Bug status controls */}
            {item.feedbackType === "bug" && item.status !== "resolved" && (
              <div className="flex items-center gap-2 pt-1">
                <span className="text-[9px] text-slate-500 font-bold">Status:</span>
                <button
                  onClick={() => updateBugStatus(item.id, "still-happening")}
                  className={`text-[9px] px-2 py-1 rounded font-bold transition-colors ${
                    item.bugStatus === "still-happening"
                      ? "bg-red-500/20 text-red-400"
                      : "bg-slate-700 text-slate-400 hover:bg-red-500/10"
                  }`}
                >
                  <AlertTriangle size={8} className="inline mr-0.5" />
                  Still Happening
                </button>
                <button
                  onClick={() => updateBugStatus(item.id, "fixed")}
                  className={`text-[9px] px-2 py-1 rounded font-bold transition-colors ${
                    item.bugStatus === "fixed"
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "bg-slate-700 text-slate-400 hover:bg-emerald-500/10"
                  }`}
                >
                  <CheckCircle size={8} className="inline mr-0.5" />
                  Fixed
                </button>
              </div>
            )}

            {/* Resolution card */}
            {item.status === "resolved" && (
              <div className="mt-2 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-[9px] font-bold text-emerald-400 flex items-center gap-1">
                  <CheckCircle size={8} />
                  Resolved by {item.resolvedByAlias || item.resolvedBy || "Agent"}
                  {item.resolvedAt?.toDate && (
                    <span className="ml-auto text-[8px] opacity-60">
                      {item.resolvedAt.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  )}
                </p>
                {item.resolutionNotes && (
                  <p className="text-[10px] text-emerald-300/80 mt-1">{item.resolutionNotes}</p>
                )}
              </div>
            )}

            {/* Rating */}
            {item.rating && (
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={10}
                    className={i < item.rating ? "text-yellow-400 fill-yellow-400" : "text-slate-600"}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
